
import Clarity from '@microsoft/clarity';
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { CLARITY_PROJECT_ID } from '../../../project_config';
import { api } from "../../../utils/api";
import { canShowDetailManagement } from "../../../utils/detailManagementAccess";
import { LoginHelper } from "../../../utils/LoginHelper";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import Footer from "../../common/footer/Footer";
import { FooterButton } from "../../common/footer/FooterButton";
import Header from "../../common/header/Header";
import Skeleton from "../../common/loading/Skeleton";
import SummarySection from "../common/summary/SummarySection";
import ActionSection from "./action/ActionSection";
import ConfirmSection from "./confirm/ConfirmSection";
import EnrollSection from "./enroll/EnrollSection";
import EpisodeSection from "./episode/EpisodeSection";
import InfoSection from "./info/InfoSection";

export default function WorkshopDetail() {
    Clarity.init(CLARITY_PROJECT_ID);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
    const params = useParams();
    const workshopId = params.workshopId;
    const [workshop, setWorkshop] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const linkDataList = [
        { label: 'خانه', href: '/' },
        { label: 'کارگاه‌ها', href: '/workshop_list/' },
        { label: workshop?.title || 'کارگاه' },
    ];

    useEffect(() => {
        fetchWorkshop();
    }, [workshopId]);

    useEffect(() => {
        document.title = `کارگاه ${workshop?.title || ''} | همراهتیم`;
    }, [workshop]);

    function fetchWorkshop() {
        setIsLoading(true);
        api.workshopDetail({
            token: LoginHelper.getToken(),
            workshop_id: workshopId,
        }).then((result) => {
            setWorkshop(result);
            setIsLoading(false);
        }).catch((err) => {
            setIsLoading(false);
            if (err && err.data) {
                setErrorMessage(err.data.message);
            }
        })
    }

    const canManageWorkshop = workshop && canShowDetailManagement(workshop.is_owner);

    const sidebarBlocks = workshop ? (
        <>
            <EnrollSection workshop={workshop} />
            {canManageWorkshop && <ActionSection workshop={workshop} />}
            {workshop.is_owner && <ConfirmSection workshop={workshop} />}
        </>
    ) : null;

    return (
        <>
            <Helmet>
                <title>کارگاه {workshop?.title || ''} | همراهتیم</title>
                <meta name="description" content={`کارگاه ${workshop?.title || ''} همراهتیم`} />
            </Helmet>

            <div className="bg-[#F0F6FC] min-h-screen flex flex-col justify-between">
                <div>
                    <Header isDesktop={isDesktop} />
                    <div className="bg-white">
                        <div className="mx-auto w-full max-w-[1200px] px-[10px] md:px-4">
                            <Breadcrumb linkDataList={linkDataList} />
                        </div>
                    </div>

                    <div className="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-5 overflow-x-hidden px-[10px] pb-8 pt-6 md:px-4 md:pt-8">
                        {isLoading && (
                            <Skeleton variant="detail" type="workshop" />
                        )}

                        {errorMessage && !workshop && (
                            <div className="rounded-[14px] border border-red-200 bg-white px-4 py-6 text-center font-Vazir-400 text-[#cc0000]">
                                {errorMessage}
                            </div>
                        )}

                        {workshop && (
                            isDesktop ? (
                                <div className="flex flex-row gap-5 items-start">
                                    <div className="flex min-w-0 flex-1 flex-col gap-5">
                                        <SummarySection workshop={workshop} />
                                        <InfoSection workshop={workshop} />
                                        <EpisodeSection
                                            workshop={workshop}
                                            onReloadNeeded={fetchWorkshop}
                                        />
                                    </div>
                                    <div className="flex w-[300px] shrink-0 flex-col gap-4 sticky top-4">
                                        {sidebarBlocks}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-5">
                                    <SummarySection workshop={workshop} />
                                    <EnrollSection workshop={workshop} />
                                    {canManageWorkshop && <ActionSection workshop={workshop} />}
                                    {workshop.is_owner && <ConfirmSection workshop={workshop} />}
                                    <InfoSection workshop={workshop} />
                                    <EpisodeSection workshop={workshop} onReloadNeeded={fetchWorkshop} />
                                </div>
                            )
                        )}
                    </div>
                </div>
                <div>
                    <Footer isDesktop={isDesktop} activeButton={FooterButton.WORKSHOPS} />
                </div>
            </div>
        </>
    )
}
